import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages the mount/animate/unmount lifecycle for overlay components
 * (Sheet, Dialog, Modal, Drawer) that need CSS transitions to work
 * inside shadow DOM (TailwindWrapper).
 *
 * Flow:
 *   isOpen=true  → shouldRender=true → reflow → animateIn=true  (enter animation)
 *   isOpen=false → animateIn=false (exit animation) → onTransitionEnd → shouldRender=false
 *
 * @param isOpen - Whether the component should be visible
 * @param fallbackDuration - Timeout (ms) to unmount if onTransitionEnd never fires (default: 350)
 */
export const useAnimatedMount = (isOpen: boolean, fallbackDuration = 350) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect -- Intentional: animation lifecycle requires synchronous state updates */
  // Open: mount DOM first, then trigger animation after reflow
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
    } else {
      setAnimateIn(false);
      // Fallback unmount in case transitionend doesn't fire
      fallbackTimer.current = setTimeout(() => setShouldRender(false), fallbackDuration);
      return () => clearTimeout(fallbackTimer.current);
    }
  }, [isOpen, fallbackDuration]);

  // After mount, force reflow then start enter animation
  useEffect(() => {
    if (shouldRender && isOpen && ref.current) {
      void ref.current.getBoundingClientRect();
      setAnimateIn(true);
    }
  }, [shouldRender, isOpen]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Called from onTransitionEnd — only unmounts after exit animation completes
  const onTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== ref.current) {
        return;
      }
      if (!isOpen) {
        clearTimeout(fallbackTimer.current);
        setShouldRender(false);
      }
    },
    [isOpen]
  );

  return { shouldRender, animateIn, ref, onTransitionEnd };
};
